import { useEffect, useRef, useState } from "react";
import { FileText, Video, MessageSquareQuote, Quote, ArrowUpRight, Check } from "lucide-react";

const chapters = [
  {
    id: "review-resume",
    label: "Resume",
    title: "Start with what they’ve done.",
    body: "Review experience against the role. Find relevant examples in the resume, then decide what you still need to understand.",
    icon: FileText,
  },
  {
    id: "spotlight-intros",
    label: "Spotlight",
    title: "Hear the story in their words.",
    body: "Where a published Spotlight Intro is available, watch the candidate introduce their experience and read the transcript alongside it. A human introduction, not a personality score.",
    icon: Video,
  },
  {
    id: "review-interview",
    label: "Interview",
    title: "Go beyond the headline.",
    body: "Invite candidates to an AI-conducted interview for the role. Review completed responses and transcripts to explore their examples in more depth.",
    icon: MessageSquareQuote,
  },
  {
    id: "review-sources",
    label: "Source-linked review",
    title: "Follow the answer to its source.",
    body: "Ask about candidates in the context of a job. Open linked resume and transcript passages, check the details, and carry your own judgment into the shortlist.",
    icon: Quote,
  },
];

function ReviewScene({ chapter }: { chapter: number }) {
  const [sourceOpen, setSourceOpen] = useState(false);
  useEffect(() => setSourceOpen(false), [chapter]);
  return (
    <figure className="recruit-review-scene" data-chapter={chapter}>
      <div className="recruit-scene-header">
        <span className="recruit-avatar">AM</span>
        <div>
          <strong>Avery Morgan</strong>
          <small>Product Lead · example candidate</small>
        </div>
        <span className="recruit-example-label">Review file</span>
      </div>
      <div className="recruit-scene-progress" aria-label="Evidence introduced in this example">
        {chapters.map(({ label, icon: Icon }, index) => (
          <span key={label} data-active={index <= chapter}>
            <Icon size={14} />
            <span>{label === "Source-linked review" ? "Sources" : label}</span>
          </span>
        ))}
      </div>
      <div className="recruit-scene-body" key={chapter}>
        <div className="recruit-criterion">
          <span>ROLE REQUIREMENT</span>
          <strong>Taking a product from problem to delivery</strong>
        </div>
        {chapter === 0 && (
          <div className="recruit-document">
            <div className="recruit-document-mast">
              <FileText size={22} />
              <span>Experience / Selected excerpt</span>
            </div>
            <h4>Product Lead</h4>
            <p>Discovery. Cross-functional delivery. Customer onboarding.</p>
            <p>
              <mark>
                Led a product squad of eight, taking a B2B onboarding platform from discovery to
                launch.
              </mark>
            </p>
            <div className="recruit-document-lines" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
            <span className="recruit-margin-note">
              An example to explore in the interview <ArrowUpRight size={14} />
            </span>
          </div>
        )}
        {chapter === 1 && (
          <div className="recruit-spotlight-scene">
            <div className="recruit-intro-tile">
              <img
                src="/images/marketing/avery-editorial.webp"
                alt="Editorial illustration of Avery, a fictional candidate, explaining their work."
                width={800}
                height={1000}
                loading="lazy"
              />
              <span className="recruit-intro-caption">
                <Video size={13} /> Spotlight Intro
              </span>
            </div>
            <div className="recruit-transcript">
              <span className="recruit-source-note">Avery’s introduction / excerpt</span>
              <blockquote>
                “I like turning a complicated customer problem into a small, testable first
                release.”
              </blockquote>
              <p>
                AI-generated illustration and fictional excerpt. Watch available published intros in
                the product.
              </p>
            </div>
          </div>
        )}
        {chapter === 2 && (
          <div className="recruit-interview-scene">
            <div className="recruit-question">
              <MessageSquareQuote size={18} />
              <p>Tell me about a time you had to narrow the scope of a launch.</p>
            </div>
            <span className="recruit-source-note">Candidate response / excerpt</span>
            <blockquote>
              “We agreed on one activation milestone, tested it with five customers, and used those
              findings to narrow the release.”
            </blockquote>
            <div className="recruit-response-footer">
              <Check size={14} /> Example completed response
            </div>
          </div>
        )}
        {chapter === 3 && (
          <div className="recruit-citation-scene">
            <p className="recruit-chat-question">What should I explore with Avery?</p>
            <p>
              Avery describes delivering a B2B onboarding platform and using customer feedback to
              narrow the release. Ask how the team measured the result.
            </p>
            <button
              className="recruit-citation-link"
              aria-expanded={sourceOpen}
              onClick={() => setSourceOpen(!sourceOpen)}
            >
              <FileText size={14} />{" "}
              {sourceOpen ? "Close source" : "Open supporting interview excerpt"}
              <ArrowUpRight size={14} />
            </button>
            {sourceOpen && (
              <blockquote className="recruit-open-source">
                “We agreed on one activation milestone, tested it with five customers, and used
                those findings to narrow the release.”
                <cite>Avery Morgan · Interview transcript · Fictional excerpt</cite>
              </blockquote>
            )}
            <p className="recruit-review-caution">
              A citation shows the source of a statement. It does not independently verify the
              claim.
            </p>
          </div>
        )}
      </div>
      <figcaption>One fictional candidate. Context accumulates—not an automatic score.</figcaption>
    </figure>
  );
}

export const EvidenceJourney = () => {
  const [active, setActive] = useState(0);
  const chapterRefs = useRef<Array<HTMLElement | null>>([]);
  useEffect(() => {
    const media = window.matchMedia(
      "(min-width: 900px) and (min-height: 700px) and (prefers-reduced-motion: no-preference)"
    );
    let observer: IntersectionObserver | undefined;
    const connect = () => {
      observer?.disconnect();
      if (!media.matches) return;
      const readingLine = Math.round(window.innerHeight * 0.3);
      const bottomMargin = Math.max(0, window.innerHeight - readingLine - 2);
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting)
              setActive(Number((entry.target as HTMLElement).dataset.chapter));
          });
        },
        // Pixel margins use viewport height; percentage root margins use width.
        { rootMargin: `-${readingLine}px 0px -${bottomMargin}px 0px`, threshold: 0 }
      );
      chapterRefs.current.forEach((element) => {
        if (element) observer?.observe(element);
      });
    };
    connect();
    media.addEventListener("change", connect);
    window.addEventListener("resize", connect);
    return () => {
      observer?.disconnect();
      media.removeEventListener("change", connect);
      window.removeEventListener("resize", connect);
    };
  }, []);
  return (
    <section id="workflow" className="recruit-journey" aria-labelledby="review-heading">
      <div className="recruit-shell">
        <div className="recruit-split-heading">
          <div>
            <p className="recruit-eyebrow">One role. A fuller picture.</p>
            <h2 id="review-heading">
              Get to know the person.
              <br />
              <span>Keep the evidence close.</span>
            </h2>
          </div>
          <p>
            Follow one candidate from the first resume review to the questions worth asking next.
          </p>
        </div>
        <nav className="recruit-chapter-nav" aria-label="Candidate review chapters">
          {chapters.map(({ id, label }, index) => (
            <a
              key={id}
              href={"#" + id}
              aria-current={active === index ? "step" : undefined}
              onClick={() => setActive(index)}
            >
              <span>0{index + 1}</span>
              {label}
            </a>
          ))}
        </nav>
        <div className="recruit-journey-grid">
          <div>
            {chapters.map(({ id, label, title, body, icon: Icon }, index) => (
              <article
                id={id}
                key={id}
                data-chapter={index}
                ref={(element) => {
                  chapterRefs.current[index] = element;
                }}
                className="recruit-chapter"
              >
                <div className="recruit-chapter-copy">
                  <span className="recruit-chapter-icon">
                    <Icon size={22} />
                  </span>
                  <p className="recruit-eyebrow">
                    0{index + 1} / {label}
                  </p>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
                <div className="recruit-mobile-scene">
                  <ReviewScene chapter={index} />
                </div>
              </article>
            ))}
          </div>
          <div className="recruit-desktop-scene">
            <ReviewScene chapter={active} />
          </div>
        </div>
      </div>
    </section>
  );
};
